import Box from "@/components/Box";
import Group from "@/tpo/Group";
import LinkWrapper from "@/tpo/LinkWrapper";
import styled, { css } from "styled-components";

import DashboardCircleArrow from "./DashboardCircleArrow";
import {
  cmpHomeDashboardSectionDescription,
  cmpHomeDashboardSectionLeadingBg,
  cmpHomeDashboardSectionPadding,
  cmpHomeDashboardTopSectionBorderRadius,
  cmpHomeDashboardSectionRowGap,
  cmpHomeDashboardSectionTitle
} from "./dashboardLayout";

const dashboardPanelSectionStyles = css`
  display: block;
  width: 100%;
  padding: ${({ isTopSection, isLastSection }) => {
    const p = cmpHomeDashboardSectionPadding;
    if (isTopSection) {
      return isLastSection ? `${p} ${p} 0 ${p}` : p;
    }
    if (isLastSection) {
      return `${p} 0 0`;
    }
    return `${p} 0`;
  }};
  color: inherit;
  text-decoration: none;
  background-color: ${({ isTopSection, topSectionBackgroundColor }) =>
    isTopSection ? topSectionBackgroundColor : "transparent"};
  border-radius: ${({ isTopSection }) =>
    isTopSection ? `${cmpHomeDashboardTopSectionBorderRadius}px` : "0"};
  border-bottom: ${({ hasDivider, theme: t }) =>
    hasDivider ? `1px solid ${t.colors.lightGrey}` : "none"};
`;

const DashboardPanelSectionLink = styled(LinkWrapper).withConfig({
  shouldForwardProp: prop =>
    prop !== "hasDivider" &&
    prop !== "isTopSection" &&
    prop !== "isLastSection" &&
    prop !== "topSectionBackgroundColor"
})`
  ${dashboardPanelSectionStyles}
`;

const DashboardPanelSectionButton = styled.button.withConfig({
  shouldForwardProp: prop =>
    prop !== "hasDivider" &&
    prop !== "isTopSection" &&
    prop !== "isLastSection" &&
    prop !== "topSectionBackgroundColor"
})`
  ${dashboardPanelSectionStyles}
  border: none;
  border-bottom: ${({ hasDivider, theme: t }) =>
    hasDivider ? `1px solid ${t.colors.lightGrey}` : "none"};
  cursor: pointer;
  text-align: left;
  font: inherit;
`;

const DashboardPanelSectionDisabled = styled(Box).withConfig({
  shouldForwardProp: prop =>
    prop !== "hasDivider" &&
    prop !== "isTopSection" &&
    prop !== "isLastSection" &&
    prop !== "topSectionBackgroundColor"
})`
  ${dashboardPanelSectionStyles}
  opacity: 0.45;
  cursor: not-allowed;
`;

const DashboardPanelSectionAnchor = styled.a.withConfig({
  shouldForwardProp: prop =>
    prop !== "hasDivider" &&
    prop !== "isTopSection" &&
    prop !== "isLastSection" &&
    prop !== "topSectionBackgroundColor"
})`
  ${dashboardPanelSectionStyles}
  border-bottom: ${({ hasDivider, theme: t }) =>
    hasDivider ? `1px solid ${t.colors.lightGrey}` : "none"};
  cursor: pointer;
`;

export default function DashboardPanelSections({
  sections,
  topSectionBackgroundColor = cmpHomeDashboardSectionLeadingBg,
  sectionLeadingPlaceholder
}) {
  return (
    <Box display="flex" flexDirection="column" width="100%">
      {sections.map((section, index) => {
        const showLeadingPlaceholder = sectionLeadingPlaceholder && !section.startIcon;
        const leadingGap = section.startIcon
          ? section.startIconGap ?? 0
          : showLeadingPlaceholder
          ? sectionLeadingPlaceholder.gap
          : 0;

        const sectionContent = (
          <Group
            width="100%"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={cmpHomeDashboardSectionRowGap}
          >
            <Group alignItems="flex-start" flex="1" minWidth={0} gap={leadingGap}>
              {section.startIcon}
              {showLeadingPlaceholder ? (
                <Box
                  width={sectionLeadingPlaceholder.size}
                  height={sectionLeadingPlaceholder.size}
                  minWidth={sectionLeadingPlaceholder.size}
                  borderRadius={sectionLeadingPlaceholder.borderRadius}
                  bg={sectionLeadingPlaceholder.bg ?? topSectionBackgroundColor}
                  flexShrink={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {section.leadingPlaceholderContent}
                </Box>
              ) : null}
              <Box display="flex" flexDirection="column" flex="1" minWidth={0}>
                <Group alignItems="center" gap={8}>
                  {section.titleLeading}
                  <Box {...cmpHomeDashboardSectionTitle}>{section.title}</Box>
                </Group>
                <Box {...cmpHomeDashboardSectionDescription}>{section.description}</Box>
              </Box>
            </Group>
            <DashboardCircleArrow linked={false} />
          </Group>
        );
        const sectionProps = {
          hasDivider: index < sections.length - 1,
          isTopSection: index === 0,
          isLastSection: index === sections.length - 1,
          topSectionBackgroundColor
        };

        if (section.disabled) {
          return (
            <DashboardPanelSectionDisabled
              key={section.title}
              aria-disabled="true"
              {...sectionProps}
            >
              {sectionContent}
            </DashboardPanelSectionDisabled>
          );
        }

        if (section.onClick) {
          return (
            <DashboardPanelSectionButton
              key={section.title}
              type="button"
              onClick={section.onClick}
              {...sectionProps}
            >
              {sectionContent}
            </DashboardPanelSectionButton>
          );
        }

        if (section.href) {
          return (
            <DashboardPanelSectionAnchor
              key={section.title}
              href={section.href}
              target="_blank"
              rel="noopener noreferrer"
              {...sectionProps}
            >
              {sectionContent}
            </DashboardPanelSectionAnchor>
          );
        }

        return (
          <DashboardPanelSectionLink key={section.title} to={section.to} {...sectionProps}>
            {sectionContent}
          </DashboardPanelSectionLink>
        );
      })}
    </Box>
  );
}
